import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Dropzone from "react-dropzone";
import TipTapEditor from "../../../TipTap/TipTapEditor";
import ToggleRow from "../../../common/ToggleRow";
import AgentConfigSection from "../AgentConfigSection";
import {
  KNOWLEDGE_INDEX_STATE_COPY,
  KNOWLEDGE_INDEX_STATE_LABELS,
  normalizeKnowledgeIndexStatus,
} from "../knowledgeStatus";
import { normalizeKnowledgeTables } from "../agentConfigDraftModel";

export default function KnowledgeTab(props) {
  const {
    name,
    knowledgeFiles,
    knowledgeTables,
    setKnowledgeTables,
    knowledgeManualContext,
    setKnowledgeManualContext,
    knowledgeIndexStatus,
    uploadingFile,
    indexingKnowledge,
    deletingKnowledgeIndex,
    databaseList,
    loadingDatabases,
    handleFileDrop,
    handleFileDelete,
    handleSyncKnowledge,
    handleDeleteKnowledgeIndex,
    ACCEPTED_KNOWLEDGE_FORMATS,
    MAX_KNOWLEDGE_FILES,
    isCreateMode,
  } = props;

  const normalizedKnowledgeStatus =
    normalizeKnowledgeIndexStatus(knowledgeIndexStatus);
  const normalizedKnowledgeTables = normalizeKnowledgeTables(knowledgeTables);
  const knowledgeTableById = new Map();
  databaseList.forEach((option) => {
    const databaseId = String(
      option?.databaseId || option?.tableId || ""
    ).trim();
    if (!databaseId) return;
    knowledgeTableById.set(databaseId, {
      databaseId,
      tableName: option?.name || option?.tableName || databaseId,
    });
  });
  normalizedKnowledgeTables.forEach((table) => {
    if (table?.databaseId && !knowledgeTableById.has(table.databaseId)) {
      knowledgeTableById.set(table.databaseId, table);
    }
  });
  const selectableKnowledgeTableOptions = Array.from(
    knowledgeTableById.values()
  );
  const selectedKnowledgeTableValues = normalizedKnowledgeTables
    .map((table) => knowledgeTableById.get(table.databaseId) || table)
    .filter((table) => !!table?.databaseId);
  const knowledgeIndexState = normalizedKnowledgeStatus.state;
  const hasKnowledgeConfigured =
    knowledgeFiles.length > 0 ||
    normalizedKnowledgeTables.length > 0 ||
    Boolean((knowledgeManualContext || "").trim());
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const statusLabel =
    KNOWLEDGE_INDEX_STATE_COPY[knowledgeIndexState] ||
    KNOWLEDGE_INDEX_STATE_COPY.NO_KNOWLEDGE;
  const statusChipColor =
    knowledgeIndexState === "READY"
      ? "success.main"
      : knowledgeIndexState === "FAILED"
      ? "error.main"
      : knowledgeIndexState === "INDEXING"
      ? "info.main"
      : knowledgeIndexState === "STALE"
      ? "warning.dark"
      : "text.secondary";
  const phaseLabelMap = {
    ASSEMBLING: "Assembling sources",
    INDEXING: "Building vector index",
    COMPLETED: "Completed",
    FAILED: "Failed",
    NO_KNOWLEDGE: "No knowledge content",
  };
  const phaseLabel =
    phaseLabelMap[normalizedKnowledgeStatus.phase] ||
    normalizedKnowledgeStatus.phase ||
    null;
  const parsedProgressPercent = Number(
    normalizedKnowledgeStatus.progressPercent
  );
  const progressPercent = Number.isFinite(parsedProgressPercent)
    ? Math.max(0, Math.min(100, Math.round(parsedProgressPercent)))
    : null;
  const showProgressBar =
    knowledgeIndexState === "INDEXING" || indexingKnowledge;
  const parseOptionalCount = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return Math.max(0, Math.round(parsed));
  };
  const sourcesTotal = parseOptionalCount(
    normalizedKnowledgeStatus.sourcesTotal
  );
  const sourcesProcessed = parseOptionalCount(
    normalizedKnowledgeStatus.sourcesProcessed
  );
  const filesTotal = parseOptionalCount(normalizedKnowledgeStatus.filesTotal);
  const tablesTotal = parseOptionalCount(normalizedKnowledgeStatus.tablesTotal);
  const workloadSegments = [];
  if (Number.isFinite(filesTotal)) {
    workloadSegments.push(`${filesTotal} file${filesTotal === 1 ? "" : "s"}`);
  }
  if (Number.isFinite(tablesTotal)) {
    workloadSegments.push(
      `${tablesTotal} table${tablesTotal === 1 ? "" : "s"}`
    );
  }
  if (normalizedKnowledgeStatus.hasManualContext === true) {
    workloadSegments.push("manual context");
  }
  const workloadSummary =
    workloadSegments.length > 0
      ? `Workload: ${workloadSegments.join(" \u00b7 ")}`
      : null;

  const hasFiles = knowledgeFiles.length > 0;
  const hasTables = normalizedKnowledgeTables.length > 0;
  const hasManualContext = Boolean((knowledgeManualContext || "").trim());

  const [filesEnabled, setFilesEnabled] = useState(hasFiles);
  const [tablesEnabled, setTablesEnabled] = useState(hasTables);
  const [manualContextEnabled, setManualContextEnabled] =
    useState(hasManualContext);

  const prevFilesCountRef = useRef(knowledgeFiles.length);
  const prevTablesCountRef = useRef(normalizedKnowledgeTables.length);
  const prevManualContextRef = useRef(hasManualContext);

  useEffect(() => {
    if (hasFiles) setFilesEnabled(true);
  }, [hasFiles]);

  useEffect(() => {
    if (hasTables) setTablesEnabled(true);
  }, [hasTables]);

  useEffect(() => {
    if (hasManualContext) setManualContextEnabled(true);
  }, [hasManualContext]);

  useEffect(() => {
    if (prevFilesCountRef.current > 0 && knowledgeFiles.length === 0) {
      setFilesEnabled(false);
    }
    prevFilesCountRef.current = knowledgeFiles.length;
  }, [knowledgeFiles.length]);

  useEffect(() => {
    if (
      prevTablesCountRef.current > 0 &&
      normalizedKnowledgeTables.length === 0
    ) {
      setTablesEnabled(false);
    }
    prevTablesCountRef.current = normalizedKnowledgeTables.length;
  }, [normalizedKnowledgeTables.length]);

  useEffect(() => {
    if (prevManualContextRef.current && !hasManualContext) {
      setManualContextEnabled(false);
    }
    prevManualContextRef.current = hasManualContext;
  }, [hasManualContext]);

  const handleFilesEnabledChange = (enabled) => {
    if (!enabled && hasFiles) return;
    setFilesEnabled(enabled);
  };

  const handleTablesEnabledChange = (enabled) => {
    setTablesEnabled(enabled);
    if (!enabled) {
      setKnowledgeTables([]);
    }
  };

  const handleManualContextEnabledChange = (enabled) => {
    setManualContextEnabled(enabled);
    if (!enabled) {
      setKnowledgeManualContext("");
    }
  };

  return (
    <>
      <AgentConfigSection id="indexStatus">
      <Box
        sx={{
          mb: 2.5,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          backgroundColor: "action.hover",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography className="org-field-label" sx={{ mb: 0 }}>
            Index Status
          </Typography>
          <Chip
            size="small"
            label={
              KNOWLEDGE_INDEX_STATE_LABELS[knowledgeIndexState] ||
              knowledgeIndexState
            }
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: statusChipColor,
              borderColor: statusChipColor,
              backgroundColor: "background.paper",
              borderRadius: "4px",
              height: 22,
              "& .MuiChip-label": { px: 1 },
            }}
            variant="outlined"
          />
        </Box>
        <Typography sx={{ fontSize: "12px", color: "text.secondary", mb: 0.5 }}>
          {statusLabel}
        </Typography>
        {(phaseLabel || normalizedKnowledgeStatus.message) && (
          <Typography
            sx={{ fontSize: "11px", color: "text.secondary", mb: 0.5 }}
          >
            {[phaseLabel, normalizedKnowledgeStatus.message]
              .filter((segment) => !!segment)
              .join(" \u00b7 ")}
          </Typography>
        )}
        {showProgressBar && (
          <Box sx={{ mb: 0.75 }}>
            <LinearProgress
              variant={
                Number.isFinite(progressPercent)
                  ? "determinate"
                  : "indeterminate"
              }
              value={
                Number.isFinite(progressPercent) ? progressPercent : undefined
              }
              sx={{
                height: 6,
                borderRadius: 'var(--status-chip-radius, 5px)',
                backgroundColor: "divider",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 'var(--status-chip-radius, 5px)',
                  backgroundColor: statusChipColor,
                },
              }}
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.secondary", mt: 0.5 }}
            >
              {Number.isFinite(progressPercent)
                ? `Progress: ${progressPercent}%`
                : "Progress: running..."}
              {Number.isFinite(sourcesProcessed) ||
              Number.isFinite(sourcesTotal)
                ? ` \u00b7 Sources: ${
                    Number.isFinite(sourcesProcessed) ? sourcesProcessed : 0
                  }/${Number.isFinite(sourcesTotal) ? sourcesTotal : "?"}`
                : ""}
            </Typography>
          </Box>
        )}
        {workloadSummary && (
          <Typography
            sx={{ fontSize: "11px", color: "text.secondary", mb: 0.5 }}
          >
            {workloadSummary}
          </Typography>
        )}
        {normalizedKnowledgeStatus.lastError && (
          <Typography sx={{ fontSize: "11px", color: "error.main", mb: 0.5 }}>
            {normalizedKnowledgeStatus.lastError}
          </Typography>
        )}
        {normalizedKnowledgeStatus.lastIndexedAt && (
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", mb: 0.75 }}
          >
            Last indexed:{" "}
            {new Date(normalizedKnowledgeStatus.lastIndexedAt).toLocaleString()}
            {Number.isFinite(normalizedKnowledgeStatus.chunksIndexed)
              ? ` · ${normalizedKnowledgeStatus.chunksIndexed} chunks`
              : ""}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSyncKnowledge}
            disabled={
              isCreateMode || indexingKnowledge || !hasKnowledgeConfigured
            }
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              borderColor: "divider",
              color: "text.secondary",
              borderRadius: "6px",
              "&:hover": {
                borderColor: "text.disabled",
                backgroundColor: "action.hover",
              },
            }}
          >
            {indexingKnowledge ? "Syncing..." : "Sync now"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeleteKnowledgeIndex}
            disabled={
              isCreateMode ||
              deletingKnowledgeIndex ||
              knowledgeIndexState === "NO_KNOWLEDGE"
            }
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              borderColor: "divider",
              color: "text.secondary",
              borderRadius: "6px",
              "&:hover": { borderColor: "error.main", color: "error.main" },
            }}
          >
            {deletingKnowledgeIndex ? "Deleting..." : "Delete index"}
          </Button>
        </Box>
        <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}>
          {isCreateMode
            ? "Save the agent first to enable index operations."
            : !hasKnowledgeConfigured
            ? "Add files, tables, or manual context, then save to build an index."
            : "Knowledge changes are synced automatically on save. Use Sync now to re-run indexing manually."}
        </Typography>
      </Box>
      </AgentConfigSection>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2.5 }}>
        <ToggleRow
          checked={filesEnabled}
          onChange={handleFilesEnabledChange}
          title="Files"
          description={
            hasFiles
              ? "Remove all uploaded files to turn this off."
              : "Upload documents to include in the knowledge index."
          }
        />

        {filesEnabled && (
          <AgentConfigSection id="files">
            {isCreateMode && (
              <Typography
                sx={{ fontSize: "12px", color: "text.disabled", mb: 1 }}
              >
                Save the agent first to enable file uploads.
              </Typography>
            )}
            <Dropzone
          onDrop={handleFileDrop}
          accept={ACCEPTED_KNOWLEDGE_FORMATS}
          multiple
          maxFiles={MAX_KNOWLEDGE_FILES - knowledgeFiles.length}
          disabled={
            isCreateMode ||
            uploadingFile ||
            knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
          }
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <Box
              {...getRootProps()}
              sx={{
                border: "1.5px dashed",
                borderColor: isDragActive ? "text.primary" : "divider",
                borderRadius: "8px",
                p: 2.5,
                textAlign: "center",
                cursor:
                  isCreateMode ||
                  uploadingFile ||
                  knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
                    ? "not-allowed"
                    : "pointer",
                backgroundColor: isDragActive
                  ? "action.hover"
                  : "background.paper",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "text.disabled",
                  backgroundColor: "action.hover",
                },
                opacity:
                  isCreateMode || knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
                    ? 0.5
                    : 1,
              }}
            >
              <input {...getInputProps()} />
              {uploadingFile ? (
                <CircularProgress size={20} sx={{ color: "text.secondary" }} />
              ) : (
                <>
                  <CloudUploadOutlinedIcon
                    sx={{ fontSize: 28, color: "text.disabled", mb: 0.5 }}
                  />
                  <Typography
                    sx={{ fontSize: "13px", color: "text.secondary" }}
                  >
                    Drag & drop or click to upload
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                  >
                    .csv, .json, .pdf, .xlsx, .xls, .txt, .md, .docx, .pptx —
                    Max {MAX_KNOWLEDGE_FILES} files
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Dropzone>
            {knowledgeFiles.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                {knowledgeFiles.map((file) => (
                  <Box
                    key={file.fileId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.75,
                      px: 1,
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <DescriptionOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.disabled",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "text.secondary",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.fileName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "11px", color: "text.disabled" }}
                      >
                        {file.fileType?.toUpperCase()} ·{" "}
                        {formatFileSize(file.fileSize)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleFileDelete(file.fileId)}
                      sx={{
                        color: "text.disabled",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </AgentConfigSection>
        )}

        <ToggleRow
          checked={tablesEnabled}
          onChange={handleTablesEnabledChange}
          title="Tables"
          description="Connect database tables to include as CSV in the knowledge index."
        />

        {tablesEnabled && (
          <AgentConfigSection id="tables">
            <Autocomplete
              multiple
              options={selectableKnowledgeTableOptions}
              getOptionLabel={(option) =>
                option.tableName || option.databaseId || ""
              }
              value={selectedKnowledgeTableValues}
              isOptionEqualToValue={(option, value) =>
                option.databaseId === value.databaseId
              }
              onChange={(_, newValue) => {
                setKnowledgeTables(normalizeKnowledgeTables(newValue));
              }}
              loading={loadingDatabases}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="org-styled-textfield"
                  variant="outlined"
                  placeholder="Select tables..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.databaseId}>
                  {option.tableName || option.databaseId}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.databaseId}
                    label={option.tableName || option.databaseId}
                    size="small"
                    sx={{ fontSize: "12px", borderRadius: "4px" }}
                  />
                ))
              }
              size="small"
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Table data will be included as CSV in the knowledge context.
            </Typography>
          </AgentConfigSection>
        )}

        <ToggleRow
          checked={manualContextEnabled}
          onChange={handleManualContextEnabledChange}
          title="Manual context"
          description="Add markdown instructions or reference material to the knowledge index."
        />

        {manualContextEnabled && (
          <AgentConfigSection id="manualContext">
            <TipTapEditor
              value={knowledgeManualContext}
              onChange={setKnowledgeManualContext}
              placeholder="Add additional context, instructions, or reference material..."
              markdown
              showToolbar={true}
              minHeight="120px"
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Markdown content that will be included in the agent's knowledge
              base.
            </Typography>
          </AgentConfigSection>
        )}
      </Box>
    </>
  );
}
